import { ledgerWindowFor } from '@/pages/accounting/ledgerWindow';
import type { TrialBalance, TrialBalanceRow } from '@/api/models';
import { formatGel } from '@/utils/money';
import { cn } from '@/utils/cnUtils';
import { NIL_AMOUNT } from '@/pages/accounting/statements/statementMeta';

/**
 * Pure helpers for the trial balance — every derivation the screen needs, none
 * of them arithmetic on money.
 *
 * The only figures produced here are `formatGel` renderings of integers the
 * server sent, plus **one** subtraction: the difference between the two totals,
 * used solely to say by how much they disagree when the server has already said
 * that they do. See {@link toTrialBalanceTotals}.
 */

// ── the closing-entries reading ─────────────────────────────────────────────

/**
 * Which of the two legitimate readings of a post-close trial balance is on
 * screen.
 *
 * After a year-end close both are correct answers to different questions:
 *
 * - `INCLUDED` — the position with the closing entries in it. Revenue and
 *   expense accounts are zeroed and the report agrees with the balance sheet.
 * - `EXCLUDED` — the year's trading position, as it stood before the books were
 *   closed.
 *
 * `INCLUDED` is the default because it is the ledger's actual state, which is
 * what someone reconciling against the balance sheet expects. The screen names
 * both readings and states which one it is showing, rather than offering an
 * unexplained "exclude closing entries" checkbox — that phrasing tells a
 * bookkeeper what the parameter is called, not what the report will say.
 */
export type ClosingView = 'INCLUDED' | 'EXCLUDED';

export const DEFAULT_CLOSING_VIEW: ClosingView = 'INCLUDED';

export const CLOSING_VIEWS: { id: ClosingView; labelKey: string }[] = [
    { id: 'INCLUDED', labelKey: 'trialBalance.closing.included' },
    { id: 'EXCLUDED', labelKey: 'trialBalance.closing.excluded' },
];

/** The wire parameter behind the chosen reading. */
export const excludeClosingFor = (view: ClosingView): boolean =>
    view === 'EXCLUDED';

/** The sentence under the control that says what the chosen reading means. */
export const closingNoteKey = (view: ClosingView): string =>
    view === 'EXCLUDED'
        ? 'trialBalance.closing.excludedNote'
        : 'trialBalance.closing.includedNote';

// ── rows ────────────────────────────────────────────────────────────────────

/** A render-ready row — all derivation done here, none in the View. */
export type TrialBalanceRowView = {
    row: TrialBalanceRow;
    accountId: string;
    code: string;
    name: string;
    /** En dash when this side is nil, which is every row's quiet side. */
    debitText: string;
    creditText: string;
    debitClassName: string;
    creditClassName: string;
};

const amountClassName = (isNil: boolean): string =>
    cn('text-right tabular-nums', isNil ? 'text-muted' : 'text-foreground');

/**
 * Turn the server's rows into render-ready rows.
 *
 * Order is the server's order (by account code), in place. Nothing is added,
 * removed, reordered or re-summed, and **no row's side is decided here** — the
 * server sent both columns already placed, which is the whole reason accumulated
 * depreciation lands on the credit side while staying typed as an asset.
 *
 * Exactly one side of each row is non-zero, so the other renders as an en dash.
 */
export const toTrialBalanceRows = (
    rows: TrialBalanceRow[],
): TrialBalanceRowView[] =>
    rows.map((row) => {
        const debitIsNil = row.debitTetri === 0;
        const creditIsNil = row.creditTetri === 0;
        return {
            row,
            accountId: row.accountId,
            code: row.code,
            name: row.name,
            debitText: debitIsNil ? NIL_AMOUNT : formatGel(row.debitTetri),
            creditText: creditIsNil ? NIL_AMOUNT : formatGel(row.creditTetri),
            debitClassName: amountClassName(debitIsNil),
            creditClassName: amountClassName(creditIsNil),
        };
    });

// ── totals ──────────────────────────────────────────────────────────────────

/** The totals row and its verdict, ready to render. */
export type TrialBalanceTotalsView = {
    debitText: string;
    creditText: string;
    /** The **server's** verdict, carried through. Never recomputed here. */
    balanced: boolean;
    /** By how much the two totals disagree. Meaningful only when unbalanced. */
    differenceText: string;
};

/**
 * The totals row.
 *
 * `balanced` is the server's own verdict and is passed through untouched — the
 * same rule the statements follow for their subtotals. The difference is
 * computed, but it is a *description* of two figures the server sent rather than
 * a second opinion about whether they agree: it is only ever shown when the
 * server has already said they do not.
 *
 * That distinction is the point of the screen. Every entry is validated as
 * balanced before it can post, so a trial balance that does not balance is an
 * engine defect rather than a bookkeeping error — which makes this verdict a
 * genuine diagnostic, and makes a client-side recomputation of it worse than
 * useless: it could only ever disagree with the ledger about the ledger.
 */
export const toTrialBalanceTotals = (
    report: TrialBalance,
): TrialBalanceTotalsView => ({
    debitText: formatGel(report.totalDebitTetri),
    creditText: formatGel(report.totalCreditTetri),
    balanced: report.balanced,
    differenceText: formatGel(
        Math.abs(report.totalDebitTetri - report.totalCreditTetri),
    ),
});

// ── drill-through ───────────────────────────────────────────────────────────

/**
 * The journal window a row drills into: **ledger start through the as-of date**.
 *
 * A trial-balance figure is a cumulative balance, not a period movement, so the
 * entries behind it are every entry for that account up to `asOf`. Sending a
 * narrower range would reach the right account and show a list that does not add
 * up to the figure the reader clicked, or none at all.
 *
 * The open start is an explicit early date, not an empty string — see
 * `ledgerWindow`, which both this and the balance sheet now share.
 */
export const ledgerRangeFor = ledgerWindowFor;
